import { getHomeOwnerWorldApi, getPropertyValuationApi } from '@/helpers/Api';
import { getAxiosClient } from '@/helpers/Axios/axiosClient';
import { matchesFunnelAbandonmentAddress } from '@/helpers/propertyValuation/funnelAbandonmentAddress';
import { MyPropertiesResult } from '@/models/queries/getMyProperties';
import { GetPropertyDataResponse, PropertyValuationResult } from '@/models/queries/getPropertyData';
import { mapHomeOwnerWorldPropertyToPropertyValuationResult } from '@/stores/propertyValuation/services';
import {
  Address,
  HomeOwnerWorldPropertiesResponse,
  HomeOwnerWorldProperty,
  HomeOwnerWorldPropertyFromValuationFunnelRequest,
  HomeOwnerWorldPropertyLinkResponse,
  HomeOwnerWorldPropertyUpdateRequest,
} from '@/stores/types/propertyValuation';
import { captureError } from '@smg-real-estate/browser-rum';
import retry, { Options } from 'async-retry';
import { AxiosResponse, HttpStatusCode } from 'axios';

export const getMyProperties = async (): Promise<MyPropertiesResult | null> => {
  try {
    const response: AxiosResponse<HomeOwnerWorldProperty[] | HomeOwnerWorldPropertiesResponse> =
      await getAxiosClient().get(getHomeOwnerWorldApi('properties'));

    const { data } = response;

    const properties = (data as HomeOwnerWorldPropertiesResponse).data || data;
    const meta = (data as HomeOwnerWorldPropertiesResponse).meta;

    return {
      data: properties.map((item) => mapHomeOwnerWorldPropertyToPropertyValuationResult(item)),
      canUserCreate: meta?.canUserCreate ?? true,
    };
  } catch (e) {
    captureError(e);
    return null;
  }
};

export const deletePropertyById = async (propertyId: string): Promise<boolean> => {
  try {
    const response: AxiosResponse = await getAxiosClient().delete(
      getHomeOwnerWorldApi(`properties/${propertyId}`),
    );
    return response.status === HttpStatusCode.Ok;
  } catch (e) {
    captureError(e);
    return false;
  }
};

export const updatePropertyById = async (
  propertyId: string,
  payload: HomeOwnerWorldPropertyUpdateRequest,
): Promise<PropertyValuationResult | null> => {
  try {
    const response: AxiosResponse<HomeOwnerWorldProperty> = await getAxiosClient().patch(
      getHomeOwnerWorldApi(`properties/${propertyId}`),
      payload,
    );

    return response?.data
      ? mapHomeOwnerWorldPropertyToPropertyValuationResult(response.data)
      : null;
  } catch (e) {
    captureError(e);
    return null;
  }
};

export enum API {
  PropertyValuation = 'property-valuation',
  HomeOwnerWorld = 'home-owner-world',
}

/**
 * For Result Page & Property Details Page, with retries 2 times
 * @param id - Either the lead ID of property valuation or the property ID of home owner world
 * @param retries - Number of retries for the request
 * @param api - API to fetch data from (PropertyValuation or HomeOwnerWorld)
 * @returns Property valuation data or null if failed
 */
export async function fetchPropertyData(
  id: string,
  retries: number,
  api: API,
): Promise<
  GetPropertyDataResponse<GetPropertyDataResponse<PropertyValuationResult | HomeOwnerWorldProperty>>
> {
  const apiEndpoint =
    api === API.HomeOwnerWorld
      ? getHomeOwnerWorldApi(`properties/${id}`) // propertyId from the HOW Database
      : getPropertyValuationApi(`result/${id}`); // leadId from the PV Database
  return await retry(
    async (bail, attempt) => {
      try {
        const response: AxiosResponse<
          GetPropertyDataResponse<
            GetPropertyDataResponse<PropertyValuationResult | HomeOwnerWorldProperty>
          >
        > = await getAxiosClient().get(apiEndpoint);
        const { data } = response || {};

        if (!data?.appraisalPrice) {
          // eslint-disable-next-line max-depth
          if (attempt > retries) {
            const error = new Error(
              `Retries exhausted, result data is invalid. id: ${id} for the api: ${api}`,
            );
            captureError(error);
            throw error;
          }
          throw new Error('Retrying ...'); // Retry the request
        }

        return api === API.HomeOwnerWorld
          ? mapHomeOwnerWorldPropertyToPropertyValuationResult(data as HomeOwnerWorldProperty)
          : data;
      } catch (error) {
        if (error?.response?.status === HttpStatusCode.NotFound) {
          bail(error);
          return null;
        }

        if (attempt > retries) {
          captureError(error);
          bail(error);
          return null;
        }

        throw error; // Retry the request
      }
    },
    { retries, minTimeout: 1000, maxTimeout: 1000 } as Options,
  );
}

export const getMatchingPropertyIdForFunnelAbandonment = async (
  funnelAddress: Address,
): Promise<string | null> => {
  const properties = await getMyProperties();
  if (!properties?.data) {
    return null;
  }

  const matchingProperty = properties.data.find((property) =>
    matchesFunnelAbandonmentAddress(funnelAddress, {
      street: property.street,
      postalCode: property.zipCode,
      city: property.town,
    }),
  );

  return matchingProperty?.leadId ?? null;
};

export const createPropertyFromValuationFunnel = async (
  payload: HomeOwnerWorldPropertyFromValuationFunnelRequest,
): Promise<{ isCreated: boolean; propertyId: string }> => {
  try {
    const response: AxiosResponse<HomeOwnerWorldProperty> = await getAxiosClient().post(
      getHomeOwnerWorldApi('properties/from-valuation-funnel'),
      payload,
    );

    return {
      isCreated: response.status === HttpStatusCode.Created,
      propertyId: response.data.propertyId,
    };
  } catch (e) {
    captureError(e);
    return { isCreated: false, propertyId: '' };
  }
};

export const connectPropertyToAccountAfterLogin = async (
  propertyId: string,
): Promise<AxiosResponse<HomeOwnerWorldPropertyLinkResponse>> => {
  try {
    return await getAxiosClient().put(
      getHomeOwnerWorldApi(`property-valuation/lead/${propertyId}/link`),
      {},
    );
  } catch (error) {
    captureError(error);
    return {
      status: HttpStatusCode.InternalServerError,
    } as AxiosResponse<HomeOwnerWorldPropertyLinkResponse>;
  }
};
